import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, FileText, Receipt, Download, Send, Eye,
  X, Trash2, Check, Clock, AlertCircle, Euro
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const quoteStatusOptions = [
  { value: 'draft', label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  { value: 'sent', label: 'Envoyé', color: 'bg-blue-100 text-blue-600' },
  { value: 'accepted', label: 'Accepté', color: 'bg-green-100 text-green-600' },
  { value: 'rejected', label: 'Refusé', color: 'bg-red-100 text-red-600' },
  { value: 'expired', label: 'Expiré', color: 'bg-amber-100 text-amber-600' },
];

const invoiceStatusOptions = [
  { value: 'draft', label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  { value: 'sent', label: 'Envoyée', color: 'bg-blue-100 text-blue-600' },
  { value: 'paid', label: 'Payée', color: 'bg-green-100 text-green-600' },
  { value: 'overdue', label: 'En retard', color: 'bg-red-100 text-red-600' },
  { value: 'cancelled', label: 'Annulée', color: 'bg-slate-100 text-slate-600' },
];

export default function BillingPage() {
  const { user } = useOutletContext();
  const [activeTab, setActiveTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newDocument, setNewDocument] = useState({
    client_name: '',
    client_email: '',
    client_company: '',
    client_address: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      if (activeTab === 'quotes') {
        const response = await fetch(`${API_URL}/api/crm/quotes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) setQuotes(await response.json());
      } else {
        const response = await fetch(`${API_URL}/api/crm/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) setInvoices(await response.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('client_token');

    // Calculate totals
    const items = newDocument.items.map(item => ({
      ...item,
      total: item.quantity * item.unit_price
    }));

    try {
      const response = await fetch(`${API_URL}/api/crm/quotes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_name: newDocument.client_name,
          client_email: newDocument.client_email,
          client_company: newDocument.client_company,
          client_address: newDocument.client_address,
          notes: newDocument.notes,
          items: items
        })
      });

      if (response.ok) {
        toast.success('Devis créé avec succès');
        setShowCreateModal(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateStatus = async (id, status, type) => {
    const token = localStorage.getItem('client_token');
    const endpoint = type === 'quote' ? 'quotes' : 'invoices';

    try {
      const response = await fetch(`${API_URL}/api/crm/${endpoint}/${id}/status?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCreateInvoiceFromQuote = async (quoteId) => {
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/crm/invoices/from-quote/${quoteId}?client_id=auto`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Facture créée depuis le devis');
        setActiveTab('invoices');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la création de la facture');
    }
  };

  const resetForm = () => {
    setNewDocument({
      client_name: '',
      client_email: '',
      client_company: '',
      client_address: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      notes: ''
    });
  };

  const addItem = () => {
    setNewDocument({
      ...newDocument,
      items: [...newDocument.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    setNewDocument({
      ...newDocument,
      items: newDocument.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const items = [...newDocument.items];
    items[index][field] = value;
    setNewDocument({ ...newDocument, items });
  };

  const calculateTotal = () => {
    const subtotal = newDocument.items.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0);
    const tax = subtotal * 0.2;
    return { subtotal, tax, total: subtotal + tax };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getStatusInfo = (status, type) => {
    const options = type === 'quote' ? quoteStatusOptions : invoiceStatusOptions;
    return options.find(s => s.value === status) || options[0];
  };

  const filteredQuotes = quotes.filter(q =>
    q.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.quote_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredInvoices = invoices.filter(i =>
    i.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  const totals = calculateTotal();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Devis & Factures</h1>
          <p className="text-rm-text-secondary">Gère tes documents commerciaux</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-rm-primary hover:bg-rm-primary/90"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouveau devis
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('quotes')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'quotes'
              ? 'border-rm-primary text-rm-primary'
              : 'border-transparent text-rm-text-secondary hover:text-rm-text'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Devis ({quotes.length})
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'invoices'
              ? 'border-rm-primary text-rm-primary'
              : 'border-transparent text-rm-text-secondary hover:text-rm-text'
          }`}
        >
          <Receipt className="w-4 h-4 inline mr-2" />
          Factures ({invoices.length})
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Rechercher un ${activeTab === 'quotes' ? 'devis' : 'facture'}...`}
          className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
        />
      </div>

      {/* Quotes List */}
      {activeTab === 'quotes' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          {filteredQuotes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-rm-text-secondary">Aucun devis trouvé</p>
              <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-rm-primary">
                <Plus className="w-4 h-4 mr-2" /> Créer un devis
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">N° Devis</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Client</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Date</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Montant</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Statut</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredQuotes.map((quote) => {
                    const statusInfo = getStatusInfo(quote.status, 'quote');
                    return (
                      <tr key={quote.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-rm-text">{quote.quote_number}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-rm-text">{quote.client_name}</p>
                            <p className="text-xs text-rm-text-secondary">{quote.client_company}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-rm-text-secondary">
                          {formatDate(quote.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-rm-text">{quote.total?.toLocaleString()}€</span>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={quote.status}
                            onChange={(e) => handleUpdateStatus(quote.id, e.target.value, 'quote')}
                            className={`text-xs px-2 py-1 rounded-full border-0 ${statusInfo.color}`}
                          >
                            {quoteStatusOptions.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {quote.status === 'accepted' && (
                              <button
                                onClick={() => handleCreateInvoiceFromQuote(quote.id)}
                                className="p-1.5 hover:bg-green-100 rounded text-green-600"
                                title="Convertir en facture"
                              >
                                <Receipt className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleUpdateStatus(quote.id, 'sent', 'quote')}
                              className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                              title="Marquer comme envoyé"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invoices List */}
      {activeTab === 'invoices' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-rm-text-secondary">Aucune facture trouvée</p>
              <p className="text-sm text-slate-400 mt-2">Crée d'abord un devis, puis convertissez-le en facture</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">N° Facture</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Client</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Date</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Montant</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Statut</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredInvoices.map((invoice) => {
                    const statusInfo = getStatusInfo(invoice.status, 'invoice');
                    return (
                      <tr key={invoice.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-rm-text">{invoice.invoice_number}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-rm-text">{invoice.client_name}</p>
                            <p className="text-xs text-rm-text-secondary">{invoice.client_company}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-rm-text-secondary">
                          {formatDate(invoice.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-rm-text">{invoice.total?.toLocaleString()}€</span>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={invoice.status}
                            onChange={(e) => handleUpdateStatus(invoice.id, e.target.value, 'invoice')}
                            className={`text-xs px-2 py-1 rounded-full border-0 ${statusInfo.color}`}
                          >
                            {invoiceStatusOptions.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleUpdateStatus(invoice.id, 'paid', 'invoice')}
                              className="p-1.5 hover:bg-green-100 rounded text-green-600"
                              title="Marquer comme payée"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(invoice.id, 'sent', 'invoice')}
                              className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                              title="Marquer comme envoyée"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Quote Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-rm-text">Nouveau devis</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateQuote} className="p-6 space-y-6">
                {/* Client Info */}
                <div>
                  <h3 className="text-sm font-medium text-rm-text mb-3">Informations client</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-rm-text-secondary mb-1">Nom *</label>
                      <input
                        type="text"
                        required
                        value={newDocument.client_name}
                        onChange={(e) => setNewDocument({ ...newDocument, client_name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-rm-text-secondary mb-1">Email</label>
                      <input
                        type="email"
                        value={newDocument.client_email}
                        onChange={(e) => setNewDocument({ ...newDocument, client_email: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-rm-text-secondary mb-1">Entreprise</label>
                      <input
                        type="text"
                        value={newDocument.client_company}
                        onChange={(e) => setNewDocument({ ...newDocument, client_company: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-rm-text-secondary mb-1">Adresse</label>
                      <input
                        type="text"
                        value={newDocument.client_address}
                        onChange={(e) => setNewDocument({ ...newDocument, client_address: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      />
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-rm-text">Lignes du devis</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {newDocument.items.map((item, index) => (
                      <div key={index} className="flex gap-3 items-start p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          />
                        </div>
                        <div className="w-20">
                          <input
                            type="number"
                            min="1"
                            placeholder="Qté"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          />
                        </div>
                        <div className="w-28">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Prix HT"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                          />
                        </div>
                        <div className="w-24 text-right py-2">
                          <span className="font-medium text-sm">{(item.quantity * item.unit_price).toFixed(2)}€</span>
                        </div>
                        {newDocument.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-2 hover:bg-red-100 rounded text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-rm-text-secondary">Sous-total HT</span>
                        <span className="font-medium">{totals.subtotal.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-rm-text-secondary">TVA (20%)</span>
                        <span className="font-medium">{totals.tax.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>Total TTC</span>
                        <span className="text-rm-primary">{totals.total.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Notes</label>
                  <textarea
                    value={newDocument.notes}
                    onChange={(e) => setNewDocument({ ...newDocument, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    rows={3}
                    placeholder="Conditions de paiement, remarques..."
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-rm-primary">
                    Créer le devis
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
